import React, { useEffect, useMemo, useState, useContext, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import PageBody from "../GeneralComponents/PageBody";
import PageBgBlock from "../GeneralComponents/PageBgBlock";
import ListEmpty from "../GeneralComponents/ListEmpty";
import InputFilter from "../GeneralComponents/InputFilter";
import GeneralContext from "../GeneralContext/GeneralContext";
import axios from "../../api/axios";

import dayjs from "dayjs";

const UI = {
  blueBand: "#CFE1F7",
  headerBlue: "#AFC8E8",
  headerHideable: "#E6CFC4",
  border: "1px solid rgba(0,0,0,0.25)",
};

const MONTHS_ES = [
  { value: "01", label: "enero" },
  { value: "02", label: "febrero" },
  { value: "03", label: "marzo" },
  { value: "04", label: "abril" },
  { value: "05", label: "mayo" },
  { value: "06", label: "junio" },
  { value: "07", label: "julio" },
  { value: "08", label: "agosto" },
  { value: "09", label: "septiembre" },
  { value: "10", label: "octubre" },
  { value: "11", label: "noviembre" },
  { value: "12", label: "diciembre" },
];

function periodoToYearMonth(periodo) {
  if (!periodo || typeof periodo !== "string") return { year: "", month: "" };
  const [y, m] = periodo.split("-");
  return { year: y ?? "", month: m ?? "" };
}

function monthKeyToLabelUpper(mk) {
  const found = MONTHS_ES.find((m) => m.value === mk);
  return (found ? found.label : mk).toUpperCase();
}

function safeNum(x) {
  if (x === null || x === undefined || Number.isNaN(Number(x))) return 0;
  return Number(x);
}

function defaultOperadorLabel(id) {
  return `OPERADOR #${id}`;
}

export default function CierreMensualRBDPorOperador() {
  const { setPageTitle } = useContext(GeneralContext);

  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    id_operador: null,
    year: null,
    month: null,
  });

  const [showHiddenColumns, setShowHiddenColumns] = useState(false);

  const operadoresById = useMemo(() => ({}), []);
  const operadorName = useCallback(
    (id) => operadoresById?.[id] ?? defaultOperadorLabel(id),
    [operadoresById]
  );

  const allColumns = [
    {
      id: "inicio_servicio",
      label: "Inicio de\nServicio",
      defaultVisible: true,
      hideable: false,
    },
    {
      id: "devengos",
      label: "Devengos",
      defaultVisible: false,
      hideable: true,
    },
    {
      id: "bajas",
      label: "Bajas",
      defaultVisible: true,
      hideable: false,
    },
    {
      id: "bajas_sin_altas",
      label: "Bajas sin altas",
      defaultVisible: false,
      hideable: true,
    },
    {
      id: "altas_inicio_mes",
      label: "Altas\n(inicio de\nmes)",
      defaultVisible: true,
      hideable: false,
    },
    {
      id: "altas_fin_mes",
      label: "Altas\n(fin de\nmes)",
      defaultVisible: true,
      hideable: true,
    },
    {
      id: "proporcionales",
      label: "Proporcionales",
      defaultVisible: false,
      hideable: true,
    },
    {
      id: "facturado_fin_de_mes",
      label: "Facturando\nfin de mes\n(código)",
      defaultVisible: true,
      hideable: true,
    },
    {
      id: "facturables_actuales",
      label: "Facturables\nactuales",
      defaultVisible: true,
      hideable: false,
    },
  ];

  const displayedColumns = useMemo(() => {
    return allColumns.filter((col) => showHiddenColumns || col.defaultVisible);
  }, [showHiddenColumns]);

  const toggleHiddenColumns = useCallback(() => {
    setShowHiddenColumns((prev) => !prev);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await axios.getConteoMensual();
      const data = Array.isArray(resp?.data) ? resp.data : [];

      const nowMonth = dayjs().startOf("month");

      const normalized = data
        .map((r, idx) => {
          const { year, month } = periodoToYearMonth(r.periodo);
          const monthStart = dayjs(r?.periodo).isValid()
            ? dayjs(r.periodo).startOf("month")
            : dayjs(`${year}-${String(month).padStart(2, "0")}-01`).startOf("month");

          return {
            ...r,
            _key: `${r.id_operador}-${r.periodo}-${idx}`,
            year,
            month,
            _monthStart: monthStart,
          };
        })
        .filter(
          (r) =>
            r._monthStart?.isValid?.() &&
            (r._monthStart.isSame(nowMonth) || r._monthStart.isBefore(nowMonth))
        );

      const lastMonth = normalized.length
        ? normalized.reduce(
            (max, cur) => (cur._monthStart.isAfter(max) ? cur._monthStart : max),
            normalized[0]._monthStart
          )
        : null;

      const finalRows = normalized.map((r) => ({
        ...r,
        is_last_month: Boolean(lastMonth && r._monthStart.isSame(lastMonth)),
      }));

      setRaw(finalRows);
    } catch (e) {
      console.error(e);
      setRaw([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    setPageTitle?.("CIERRE MENSUAL DE RBD POR OPERADOR");
  }, []);

  const filterRecords = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value == null || value === "" ? null : value,
    }));
  };

  const operadorOpt = useMemo(() => {
    const ids = [...new Set(raw.map((d) => d.id_operador))].filter((x) => x != null);
    ids.sort((a, b) => Number(a) - Number(b));
    return ids.map((id) => ({ value: String(id), label: operadorName(id) }));
  }, [raw, operadorName]);

  const yearOpt = useMemo(() => {
    const years = [...new Set(raw.map((d) => d.year))].filter(Boolean);
    years.sort((a, b) => Number(b) - Number(a));
    return years;
  }, [raw]);

  const monthOpt = useMemo(() => MONTHS_ES, []);

  const filtered = useMemo(() => {
    const nowMonth = dayjs().startOf("month");

    let arr = raw.slice();
    arr = arr.filter((r) => {
      const ms = r._monthStart;
      return ms.isSame(nowMonth) || ms.isBefore(nowMonth);
    });

    if (filters.id_operador != null) {
      arr = arr.filter((r) => String(r.id_operador) === String(filters.id_operador));
    }
    if (filters.year != null) {
      arr = arr.filter((r) => String(r.year) === String(filters.year));
    }
    if (filters.month != null) {
      arr = arr.filter((r) => String(r.month) === String(filters.month));
    }

    return arr;
  }, [raw, filters]);

  const columnsCount = displayedColumns.length;

  const groupsByOperador = useMemo(() => {
    const map = new Map();
    for (const r of filtered) {
      const k = String(r.id_operador);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    }

    const blocks = [];
    for (const [opId, rows] of map.entries()) {
      blocks.push({
        opId,
        opName: operadorName(Number(opId)).toUpperCase(),
        rows,
      });
    }

    blocks.sort((a, b) => a.opName.localeCompare(b.opName));
    return blocks;
  }, [filtered, operadorName]);

  const mode = useMemo(() => {
    if (filters.month != null && filters.year != null) return "MONTH_YEAR_SUMMARY";
    if (filters.month != null && filters.year == null) return "MONTH_ALL_YEARS";
    return "ALL_PERIODS";
  }, [filters.month, filters.year]);

  const ExpandToggleCell = ({ rowSpan = 1 }) => (
    <TableCell
      rowSpan={rowSpan}
      onClick={toggleHiddenColumns}
      sx={{
        border: UI.border,
        background: "#D9D9D9",
        width: 42,
        minWidth: 42,
        maxWidth: 42,
        textAlign: "center",
        verticalAlign: "middle",
        cursor: "pointer",
        fontWeight: 800,
        p: 0,
        userSelect: "none",
        "&:hover": {
          backgroundColor: "#D9D9D9",
        },
      }}
    >
      <Box
        sx={{
          height: "100%",
          minHeight: 80,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {!showHiddenColumns ? <ChevronRightIcon /> : <ChevronLeftIcon />}
      </Box>
    </TableCell>
  );

  const MetricRowCells = ({ row }) => {
    const valueByColumn = {
      inicio_servicio: safeNum(row?.inicio_servicio),
      devengos: safeNum(row?.devengos),
      bajas: safeNum(row?.bajas),
      bajas_sin_altas: safeNum(row?.bajas_sin_altas),
      altas_inicio_mes: safeNum(row?.altas_inicio_mes),
      altas_fin_mes: row?.is_last_month ? "-" : safeNum(row?.altas_fin_mes),
      proporcionales: safeNum(row?.proporcionales),
      facturado_fin_de_mes: row?.is_last_month ? "-" : safeNum(row?.facturado_fin_de_mes),
      facturables_actuales: safeNum(row?.facturables_actuales),
    };

    return (
      <>
        {displayedColumns.map((col) => (
          <TableCell key={col.id} align="center" sx={{ border: UI.border }}>
            {valueByColumn[col.id]}
          </TableCell>
        ))}
      </>
    );
  };

  const HeaderRow = () => (
    <TableRow>
      <TableCell sx={{ border: "none", width: 64 }} />
      <TableCell sx={{ border: UI.border, width: 140 }} />
      {displayedColumns.map((c) => (
        <TableCell
          key={c.id}
          align="center"
          sx={{
            border: UI.border,
            background: !c.defaultVisible ? UI.headerHideable : UI.headerBlue,
            fontWeight: 700,
            fontSize: "0.78rem",
            whiteSpace: "pre-line",
            lineHeight: 1.15,
            py: 1,
            minWidth: 80,
          }}
        >
          {c.label}
        </TableCell>
      ))}
      <TableCell
        sx={{
          border: "",
          width: 42,
          minWidth: 42,
          maxWidth: 42,
          p: 0,
        }}
      />
    </TableRow>
  );

  const OperatorSingleRowSection = ({ opName, row }) => {
    return (
      <>
        <TableRow>
          <TableCell sx={{ border: "none", fontWeight: 800, textAlign: "center", py: 1 }} />
          <TableCell
            colSpan={1 + columnsCount + 1}
            sx={{ border: UI.border, fontWeight: 800, textAlign: "center", py: 1 }}
          >
            {opName}
          </TableCell>
        </TableRow>

        <HeaderRow />

        <TableRow>
          <TableCell
            sx={{
              border: "1px solid white",
              borderRight: UI.border,
              background: UI.blueBand,
              width: 64,
              textAlign: "center",
              verticalAlign: "middle",
              fontWeight: 800,
              writingMode: "vertical-rl",
              transform: "rotate(180deg)",
            }}
          >
            {row?.year ?? ""}
          </TableCell>

          <TableCell
            sx={{
              border: UI.border,
              background: UI.blueBand,
              fontWeight: 800,
              textAlign: "center",
              width: 140,
            }}
          >
            {monthKeyToLabelUpper(row?.month ?? "")}
          </TableCell>

          <MetricRowCells row={row} />
          <ExpandToggleCell rowSpan={1} />
        </TableRow>
      </>
    );
  };

  const OperatorMonthAllYearsTable = ({ opName, rows, monthKey }) => {
    const monthLabel = monthKeyToLabelUpper(monthKey);

    const byYear = new Map();
    for (const r of rows) {
      if (!byYear.has(r.year)) byYear.set(r.year, r);
      else {
        const cur = byYear.get(r.year);
        const best = (r.periodo ?? "").localeCompare(cur.periodo ?? "") > 0 ? r : cur;
        byYear.set(r.year, best);
      }
    }

    const years = Array.from(byYear.keys())
      .filter(Boolean)
      .sort((a, b) => Number(b) - Number(a));
    const list = years.map((y) => byYear.get(y));

    return (
      <>
        <TableRow>
          <TableCell sx={{ border: "none", fontWeight: 800, textAlign: "center", py: 1 }} />
          <TableCell
            colSpan={1 + columnsCount + 1}
            sx={{ border: UI.border, fontWeight: 800, textAlign: "center", py: 1 }}
          >
            {opName}
          </TableCell>
        </TableRow>

        <HeaderRow />

        {list.map((r, idx) => (
          <TableRow key={r._key ?? `${opName}-${r.year}-${idx}`}>
            <TableCell
              sx={{
                border: UI.border,
                background: UI.blueBand,
                width: 64,
                textAlign: "center",
                verticalAlign: "middle",
                fontWeight: 800,
                writingMode: "vertical-rl",
                transform: "rotate(180deg)",
              }}
            >
              {r.year}
            </TableCell>

            <TableCell
              sx={{
                border: UI.border,
                background: UI.blueBand,
                fontWeight: 800,
                textAlign: "center",
                width: 140,
              }}
            >
              {monthLabel}
            </TableCell>

            <MetricRowCells row={r} />

            {idx === 0 && <ExpandToggleCell rowSpan={list.length} />}
          </TableRow>
        ))}
      </>
    );
  };

  const OperatorPeriodTable = ({ opName, rows }) => {
    const sorted = rows.slice().sort((a, b) => (b.periodo ?? "").localeCompare(a.periodo ?? ""));
    const years = [...new Set(sorted.map((r) => r.year))]
      .filter(Boolean)
      .sort((a, b) => Number(b) - Number(a));

    const flat = [];
    for (const y of years) {
      const yRows = sorted
        .filter((r) => r.year === y)
        .sort((a, b) => Number(b.month) - Number(a.month));
      flat.push({ type: "yearStart", year: y, rowSpan: yRows.length });
      for (const r of yRows) flat.push({ type: "row", row: r });
    }

    return (
      <>
        <TableRow>
          <TableCell sx={{ border: "none", fontWeight: 800, textAlign: "center", py: 1 }} />
          <TableCell
            colSpan={1 + columnsCount + 1}
            sx={{ border: UI.border, fontWeight: 800, textAlign: "center", py: 1 }}
          >
            {opName}
          </TableCell>
        </TableRow>

        <HeaderRow />

        {(() => {
          let pendingYearCell = null;
          let rowIndex = -1;
          const totalRows = flat.filter((x) => x.type === "row").length;

          return flat.map((item, idx) => {
            if (item.type === "yearStart") {
              pendingYearCell = (
                <TableCell
                  key={`Y-${opName}-${item.year}`}
                  rowSpan={item.rowSpan}
                  sx={{
                    border: "1px solid white",
                    borderRight: UI.border,
                    background: UI.blueBand,
                    width: 64,
                    textAlign: "center",
                    verticalAlign: "middle",
                    fontWeight: 800,
                    writingMode: "vertical-rl",
                    transform: "rotate(180deg)",
                  }}
                >
                  {item.year}
                </TableCell>
              );
              return null;
            }

            rowIndex += 1;
            const r = item.row;
            const monthLabel = monthKeyToLabelUpper(r.month);
            const yearCellToRender = pendingYearCell;
            pendingYearCell = null;

            return (
              <TableRow key={r._key ?? `R-${opName}-${idx}`}>
                {yearCellToRender}

                <TableCell
                  sx={{
                    border: UI.border,
                    background: UI.blueBand,
                    fontWeight: 800,
                    textAlign: "center",
                    width: 140,
                  }}
                >
                  {monthLabel}
                </TableCell>

                <MetricRowCells row={r} />

                {rowIndex === 0 && <ExpandToggleCell rowSpan={totalRows} />}
              </TableRow>
            );
          });
        })()}
      </>
    );
  };

  const singleRowBlocks = useMemo(() => {
    if (mode !== "MONTH_YEAR_SUMMARY") return [];
    return groupsByOperador
      .map((b) => {
        const pick =
          b.rows.slice().sort((a, c) => (c.periodo ?? "").localeCompare(a.periodo ?? ""))[0] ??
          null;
        return { ...b, pick };
      })
      .filter((b) => b.pick != null);
  }, [mode, groupsByOperador]);

  return (
    <PageBody>
      <Box width="100%">
        <Box
          sx={{
            alignItems: "center",
            width: "100%",
            justifyContent: "center",
          }}
        >
          <Typography variant="h1" sx={{ textAlign: "center", p: "0.6rem" }}>
            CIERRE MENSUAL DE RBD POR OPERADOR
          </Typography>
        </Box>

        <Grid container spacing={1} sx={{ mb: "18px", my: 1.5 }} columns={12}>
          <Grid size={4}>
            <InputFilter
              item="id_operador"
              name="Operador"
              data={raw}
              opt={operadorOpt}
              callback={filterRecords}
            />
          </Grid>
          <Grid size={4}>
            <InputFilter
              item="year"
              name="Año"
              data={raw}
              opt={yearOpt}
              callback={filterRecords}
            />
          </Grid>
          <Grid size={4}>
            <InputFilter
              item="month"
              name="Mes"
              data={raw}
              opt={monthOpt}
              callback={filterRecords}
            />
          </Grid>
        </Grid>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
              width: "100%",
              backgroundColor: "white",
            }}
          >
            <CircularProgress />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Cargando...
            </Typography>
          </Box>
        ) : (
          <PageBgBlock pad="0">
            <Paper
              sx={{
                width: "100%",
                background: "#fff",
                borderRadius: "0.25rem",
                mb: "0.75rem",
                p: 2,
              }}
              elevation={0}
            >
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 980, borderCollapse: "collapse" }}>
                  <TableBody>
                    {filtered.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2 + allColumns.length + 1} sx={{ border: UI.border }}>
                          <ListEmpty />
                        </TableCell>
                      </TableRow>
                    ) : mode === "MONTH_YEAR_SUMMARY" ? (
                      singleRowBlocks.map((b) => (
                        <OperatorSingleRowSection
                          key={b.opId}
                          opName={b.opName}
                          row={b.pick}
                        />
                      ))
                    ) : mode === "MONTH_ALL_YEARS" ? (
                      groupsByOperador.map((b) => (
                        <OperatorMonthAllYearsTable
                          key={b.opId}
                          opName={b.opName}
                          rows={b.rows}
                          monthKey={filters.month}
                        />
                      ))
                    ) : (
                      groupsByOperador.map((b) => (
                        <OperatorPeriodTable
                          key={b.opId}
                          opName={b.opName}
                          rows={b.rows}
                        />
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </PageBgBlock>
        )}
      </Box>
    </PageBody>
  );
}